import React, { useState } from 'react';
import { MessageSquare, Send, Check } from 'lucide-react';

const FeedbackForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'general',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name || null,
          email: formData.email || null,
          category: formData.category,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', category: 'general', message: '' });
        setTimeout(() => {
          setSubmitted(false);
          setIsOpen(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110"
        style={{ 
          background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)',
          boxShadow: '0 4px 20px rgba(91, 127, 255, 0.5)'
        }}
        aria-label="Send Feedback"
      >
        <MessageSquare size={24} className="text-white" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
          <div 
            className="max-w-lg w-full relative"
            style={{ 
              background: 'var(--glass-card)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--glass-shadow)',
            }}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              ×
            </button>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className="heading-3 mb-2">Thank You!</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Your feedback has been received and will be reviewed.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-blue-200)' }}>
                    <MessageSquare size={24} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <div>
                    <h2 className="heading-3">Send Feedback</h2>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      We'd love to hear from you
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="body-small font-medium mb-2 block">Name (optional)</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your name"
                        className="w-full px-4 py-2.5 rounded-lg border border-border-input focus:border-border-input-focus outline-none text-sm"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                    <div>
                      <label className="body-small font-medium mb-2 block">Email (optional)</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="your@email.com"
                        className="w-full px-4 py-2.5 rounded-lg border border-border-input focus:border-border-input-focus outline-none text-sm"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="body-small font-medium mb-2 block">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-lg border border-border-input focus:border-border-input-focus outline-none text-sm"
                      style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <option value="general">General Feedback</option>
                      <option value="bug">Report a Bug</option>
                      <option value="feature">Feature Request</option>
                      <option value="content">Content Suggestion</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="body-small font-medium mb-2 block">Your Feedback *</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Tell us what's on your mind..."
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none resize-none text-sm"
                      style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <Send size={18} />
                      {submitting ? 'Sending...' : 'Send Feedback'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackForm;
